import type { ReactNode } from 'react';

interface LegalLayoutProps {
  title: string;
  lastUpdated: string;
  children: ReactNode;
}

/**
 * Shared layout for the storefront's legal pages (Terms, Privacy). Renders a
 * title, a "last updated" line, the body content, and a standing draft notice.
 * Body styling is applied via arbitrary child selectors so the pages can be
 * authored as plain semantic HTML without the typography plugin.
 */
export function LegalLayout({ title, lastUpdated, children }: LegalLayoutProps) {
  return (
    <article className="mx-auto max-w-3xl px-4 py-16 sm:px-6">
      <h1 className="font-serif text-4xl font-semibold text-brand-navy">
        {title}
      </h1>
      <p className="mt-2 text-sm text-gray-500">Last updated: {lastUpdated}</p>

      <div
        className="mt-8 text-[15px] leading-relaxed text-gray-700
          [&_h2]:mt-10 [&_h2]:font-serif [&_h2]:text-xl [&_h2]:font-semibold [&_h2]:text-brand-navy
          [&_h3]:mt-6 [&_h3]:font-semibold [&_h3]:text-brand-navy
          [&_p]:mt-3 [&_ul]:mt-3 [&_ul]:list-disc [&_ul]:pl-6 [&_li]:mt-1
          [&_a]:text-brand-blue [&_a]:underline [&_strong]:text-brand-navy"
      >
        {children}
      </div>

      <p className="mt-12 border-t border-gray-200 pt-6 text-xs leading-relaxed text-gray-400">
        This page is provided for general information about BestAcco LLC and our
        services and does not constitute legal advice. We may update it from time
        to time; the “Last updated” date above reflects the current version.
      </p>
    </article>
  );
}
